import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, SimpleGrid, useToast, Flex, Button } from '@chakra-ui/react';
import DynamicTable from '../../../components/table/DynamicTable.js';
import { getData, deleteData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import CreateCourseDiscountModal from '../../../components/courseDiscounts/CreateCourseDiscountModal.js';
import ConfirmModal from '../../../components/modal/ConfirmModal.js';

// Columnas para la tabla de descuentos
const columnsDataDiscounts = [
  {
    Header: 'ID',
    accessor: 'id',
  },
  {
    Header: 'Documento',
    accessor: 'studentDocument',
  },
  {
    Header: 'Apellidos',
    accessor: 'studentLastName',
  },
  {
    Header: 'Nombres',
    accessor: 'studentFirstName',
  },
  {
    Header: 'Materia',
    accessor: 'subjectDescription',
  },
  {
    Header: 'Fecha de Aprobación',
    accessor: 'approvalDate',
    Cell: ({ value }) => {
      if (!value) return '-';
      return new Date(value).toLocaleDateString('es-UY');
    },
  },
  {
    Header: 'Porcentaje de Descuento',
    accessor: 'discountPercentage',
    Cell: ({ value }) => `${value}%`,
  },
  {
    Header: 'Concepto',
    accessor: 'concept',
  },
];

export default function CourseDiscounts() {
  const { searchQuery } = useSearch();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalState, setModalState] = useState({
    createModal: false,
    confirmDelete: false,
  });
  const [selectedDiscount, setSelectedDiscount] = useState(null);

  const rowsPerPage = 10;
  const toast = useToast();

  const latestRequestRef = useRef(0);

  const getDiscounts = useCallback(async (searchData) => {
    const requestId = ++latestRequestRef.current;
    try {
      const response = await getData(
        `/course-discounts?page=${page + 1}&limit=${rowsPerPage}&search=${searchData}`,
      );
      if (requestId !== latestRequestRef.current) return;
      setData(response?.payload?.discounts || []);
      setTotalPages(Math.ceil(response?.payload?.total / rowsPerPage));
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      toast({
        title: 'Error al obtener descuentos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [page, rowsPerPage, toast]);

  const handleCreateDiscount = async () => {
    // Recargar la lista después de crear un descuento
    await getDiscounts('');
    setModalState(prev => ({ ...prev, createModal: false }));
  };

  const handleDeleteDiscount = async () => {
    if (!selectedDiscount) return;
    
    try {
      await deleteData(`/course-discounts/${selectedDiscount.id}`);
      toast({
        title: 'Descuento eliminado exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setModalState(prev => ({ ...prev, confirmDelete: false }));
      setSelectedDiscount(null);
      await getDiscounts('');
    } catch (error) {
      toast({
        title: 'Error al eliminar descuento',
        description: error.message || 'Ha ocurrido un error inesperado',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getDiscounts(searchQuery);
  }, [page, searchQuery, getDiscounts]);

  const openCreateModal = () => {
    setModalState(prev => ({ ...prev, createModal: true }));
  };

  const closeCreateModal = () => {
    setModalState(prev => ({ ...prev, createModal: false }));
  };

  const openDeleteModal = (discount) => {
    setSelectedDiscount(discount);
    setModalState(prev => ({ ...prev, confirmDelete: true }));
  };

  const closeDeleteModal = () => {
    setModalState(prev => ({ ...prev, confirmDelete: false }));
    setSelectedDiscount(null);
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify="flex-end" mb={4}>
        <Button 
          colorScheme="blue" 
          onClick={openCreateModal}
          size="lg"
          fontWeight="bold"
        >
          Nuevo Descuento
        </Button>
      </Flex>
      
      <SimpleGrid
        mb="20px"
        columns={{ sm: 1 }}
        spacing={{ base: '20px', xl: '20px' }}
      >
        <DynamicTable
          columnsData={columnsDataDiscounts}
          data={data}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          showRowInfo={false}
          onEdit={(discount) => {
            // Aquí podrías abrir un modal de edición si lo necesitas
            console.log('Editar descuento:', discount);
          }}
          onDelete={openDeleteModal}
        />
      </SimpleGrid>

      {/* Modal para crear nuevo descuento */}
      <CreateCourseDiscountModal
        isOpen={modalState.createModal}
        onClose={closeCreateModal}
        onConfirm={handleCreateDiscount}
      />

      {/* Modal de confirmación para eliminar */}
      <ConfirmModal
        isOpen={modalState.confirmDelete}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteDiscount}
        header="Confirmar Eliminación"
        message={`¿Estás seguro de que deseas eliminar el descuento de ${selectedDiscount?.studentFirstName || ''} ${selectedDiscount?.studentLastName || ''}?`}
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
    </Box>
  );
}
